import { IDateRange } from '../models/SearchQuery';
import { normalizeInclusiveLocalDateRange } from './dateRangeUtils';

interface IReportCenter {
  centerId: string;
  state?: string;
  addressDistrict?: string;
}

interface IReportJobGrain {
  grainId?: string;
}

export interface IJobReportGrainValidation {
  isValid: boolean;
  grainId?: string;
}

export type JobReportDateField = 'createdAt' | 'updatedAt';

export interface IJobReportDateFilter extends IDateRange {
  dateField: JobReportDateField;
}

export const resolveJobReportDateFilter = (
  dateRange: IDateRange | undefined,
  dateField: JobReportDateField
): IJobReportDateFilter | undefined => {
  const normalizedRange = normalizeInclusiveLocalDateRange(dateRange);
  return normalizedRange
    ? { ...normalizedRange, dateField }
    : undefined;
};

export const validateJobReportGrainIds = (
  jobs: IReportJobGrain[]
): IJobReportGrainValidation => {
  if (jobs.length === 0) {
    return { isValid: true };
  }

  const firstGrainId = jobs[0].grainId?.trim();
  if (!firstGrainId) {
    return { isValid: false };
  }

  const isValid = jobs.every((job) => job.grainId?.trim() === firstGrainId);
  return isValid
    ? { isValid: true, grainId: firstGrainId }
    : { isValid: false };
};

export const isSameGrainJobReportError = (error: unknown): boolean => (
  error instanceof Error && error.message.toLowerCase().includes('same grain type')
);

export const resolveJobReportCenterIds = (
  centers: IReportCenter[],
  centerIdFilter: string,
  stateFilter: string,
  districtFilter: string
): string[] | undefined => {
  const hasCenterFilter = Boolean(centerIdFilter && centerIdFilter !== 'all');
  const hasStateFilter = Boolean(stateFilter && stateFilter !== 'all');
  const hasDistrictFilter = Boolean(districtFilter && districtFilter !== 'all');

  if (!hasCenterFilter && !hasStateFilter && !hasDistrictFilter) {
    return undefined;
  }

  if (hasCenterFilter && !hasStateFilter && !hasDistrictFilter) {
    return [centerIdFilter];
  }

  return centers
    .filter((center) => !hasCenterFilter || center.centerId === centerIdFilter)
    .filter((center) => !hasStateFilter || center.state === stateFilter)
    .filter((center) => !hasDistrictFilter || center.addressDistrict === districtFilter)
    .map((center) => center.centerId);
};
